import { motion } from "framer-motion";
import { ArrowUpRight, Check, Clock3, FileText } from "lucide-react";
import { Reveal, SectionHeading } from "../components/Reveal";
import { PORTAL_URL } from "../lib/api";

const TASKS = [
  { icon: <Check size={13} />, label: "Analisi esigenza completata" },
  { icon: <Clock3 size={13} />, label: "Ricerca opportunità in corso" },
  { icon: <FileText size={13} />, label: "Documentazione organizzata" },
];

export default function PiattaformaSection() {
  return (
    <section id="piattaforma" className="section overflow-hidden" data-testid="piattaforma-section">
      <div className="container-hub grid items-center gap-16 lg:grid-cols-[0.9fr_1.1fr]">
        <div>
          <SectionHeading
            eyebrow="Piattaforma digitale"
            title={<>IL TUO PERCORSO. <span className="gold-text">SEMPRE SOTTO CONTROLLO.</span></>}
            lead="Con il portale A.C. HUB segui il tuo percorso, conosci lo stato di ogni attività e hai la documentazione organizzata in un unico ambiente digitale. Nessuna call a vuoto: tutto è scritto."
          />
          <Reveal delay={0.25}>
            <a
              href={PORTAL_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="piattaforma-cta"
              className="btn btn-outline mt-9"
            >
              Scopri la piattaforma
              <ArrowUpRight size={14} />
            </a>
          </Reveal>
        </div>

        <Reveal delay={0.15}>
          <div className="relative min-h-[520px]" data-testid="device-stage">
            <span
              className="absolute left-0 top-0 z-[3] border border-[#c9a45c]/40 bg-[#0b0b0b]/90 px-3 py-1.5 text-[8px] font-bold uppercase tracking-[0.2em] text-gold-light"
              data-testid="platform-demo-badge"
            >
              Esempio dimostrativo
            </span>
            <motion.div
              className="absolute right-0 top-0 w-[88%] border border-[#e1c47a]/40 bg-[#151515] p-4 shadow-[0_38px_72px_rgba(0,0,0,0.5)] sm:p-5"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            >
              <div className="flex items-center justify-between border-b border-[#c9a45c]/20 pb-3.5 text-[10px] font-bold tracking-[0.12em]">
                <span className="text-gold-light">A.C. HUB</span>
                <span className="text-cream">AREA PERCORSO</span>
              </div>
              <div className="grid grid-cols-[130px_1fr] gap-3.5 pt-4">
                <aside className="grid content-start gap-3.5 border border-[#c9a45c]/15 bg-[#101010] p-3.5 text-[10px] text-[#a9a7a1]">
                  <span className="text-gold-light">PANORAMICA</span>
                  <span>ATTIVITÀ</span>
                  <span>DOCUMENTI</span>
                  <span>COMUNICAZIONI</span>
                </aside>
                <div className="grid gap-3">
                  <div className="border border-[#c9a45c]/15 bg-[#101010] p-4">
                    <div className="flex items-center justify-between text-[11px]">
                      <b className="text-cream">AC HUNT · PERCORSO ATTIVO</b>
                      <span className="font-bold text-gold-light">68%</span>
                    </div>
                    <div className="mt-3.5 h-1.5 bg-[#2a2927]">
                      <motion.div
                        className="h-full bg-gold"
                        initial={{ width: 0 }}
                        whileInView={{ width: "68%" }}
                        viewport={{ once: true }}
                        transition={{ duration: 1.2, delay: 0.4, ease: [0.16, 1, 0.3, 1] }}
                      />
                    </div>
                  </div>
                  <div className="border border-[#c9a45c]/15 bg-[#101010] p-4">
                    <span className="text-[11px] font-bold text-cream">ATTIVITÀ IN CORSO</span>
                    <div className="mt-3 grid gap-2">
                      {TASKS.map((t) => (
                        <div key={t.label} className="flex items-center justify-between border-t border-[#c9a45c]/12 px-1 py-2 text-[10px] text-[#bbb8b1]">
                          <span>{t.label}</span>
                          <i className="text-gold">{t.icon}</i>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>

            <motion.div
              className="absolute bottom-0 left-0 z-[2] w-[200px] rounded-[34px] border border-[#e1c47a]/60 bg-[#050505] p-3 shadow-[0_32px_60px_rgba(0,0,0,0.6)] sm:w-[228px]"
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.8, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
              animate={{ y: [0, -10, 0] }}
            >
              <div className="h-full rounded-[25px] bg-[#101010] px-4 pb-6 pt-5">
                <div className="mx-auto mb-7 h-1.5 w-[70px] rounded-full bg-[#292929]" />
                <span className="text-[9px] font-bold tracking-[0.16em] text-gold">IL TUO PERCORSO</span>
                <p className="mt-3 font-serif text-[22px] leading-tight text-cream">Sempre con te.</p>
                <div className="mt-5 border border-[#c9a45c]/22 p-4">
                  <small className="text-[9px] tracking-[0.14em] text-[#929292]">AVANZAMENTO</small>
                  <b className="mt-1.5 block text-[12px] text-cream">Ricerca attiva 68%</b>
                  <div className="mt-3.5 h-1 bg-[#292929]">
                    <span className="block h-full w-[68%] bg-gold" />
                  </div>
                </div>
                <div className="mt-3 border border-[#c9a45c]/22 p-4">
                  <small className="text-[9px] tracking-[0.14em] text-[#929292]">DOCUMENTI</small>
                  <b className="mt-1.5 block text-[12px] text-cream">Tutto ordinato, in un solo luogo.</b>
                </div>
              </div>
            </motion.div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
